/**
 * **Propagate access…** of a Data Lake directory (S8 Task 8.11): a recursive `modify` / `set` / `remove` started as a
 * Transfers job (`hns-acl-recursive`). The root's ETag is read when the dialog opens and sent as `expectedRootEtag`
 * (main re-checks it right before the first batch — finding S8-07); Replace and Remove ask for the typed directory name
 * (D4) in a confirmation INSIDE this dialog (`TypedNameConfirmPanel` — the app-level confirm dialog cannot take typing
 * over an open dialog), Merge starts on the dialog's own Start. A started job closes the
 * dialog and opens the Transfers panel; `TargetChanged` stays inline with Reload.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { STORAGE_READ_ONLY_REASON } from '../../../../../core/engines/azureStorageRefusal';
import { aclRootIdentity } from '../../../../../core/engines/azureStorageAclJobText';
import { shortEtag } from '../../../../../core/engines/azureStorageEtag';
import { hnsServiceFacts } from '../../../../../core/engines/azureStorageHnsFacts';
import type { StorageAclChange, StorageHnsAccessControl, StorageOperationError } from '../../../../../types/azureStorage';
import { useTransfersPanelStore } from '../../../../transfers/store/transfersPanelStore';
import { QueuedDialogNotice } from '../../properties/dialogs/QueuedDialogNotice';
import { useReportStorageDialogGuard } from '../../properties/dialogs/storageDialogStore';
import { hnsPathLabel } from '../aclDraft';
import { propagateConfirmParts, propagateTypedName, rootResultOf } from '../aclPropagate';
import { useHnsAccessControl } from '../hooks/useHnsAccessControl';
import { useHnsAclRecursiveStart } from '../hooks/useHnsAclRecursiveStart';
import { useHnsRootVersion } from '../hooks/useHnsRootVersion';
import { aclEditSource, trackAclEdit, type AclEditOutcome } from './aclEditAnalytics';
import type { HnsHostedDialogProps } from './hnsDialogTypes';
import { PropagateAclForm } from './PropagateAclForm';
import { TypedNameConfirmPanel } from './TypedNameConfirmPanel';

const maskKey = (acl: StorageHnsAccessControl['entries']): string => JSON.stringify(acl.filter((e) => e.type === 'mask').map((e) => [e.scope, e.permissions]).sort());
const outcomeOf = (error: StorageOperationError): AclEditOutcome =>
  error.category === 'conflict' ? 'conflict' : error.category === 'authorization' ? 'refused' : error.category === 'cancelled' ? 'cancelled' : error.category === 'ambiguous_outcome' ? 'ambiguous' : 'error';

export function PropagateAclDialog({ connectionId, resource, options, readOnly, onGuardChange, onClose }: HnsHostedDialogProps) {
  const query = useHnsAccessControl({ connectionId, resource });
  const version = useHnsRootVersion(connectionId, resource);
  const starter = useHnsAclRecursiveStart(connectionId);
  const [rootAcl, setRootAcl] = useState<{ acl: StorageHnsAccessControl; key: number }>();
  const [dirty, setDirty] = useState(false);
  const [failure, setFailure] = useState<StorageOperationError>();
  const [confirming, setConfirming] = useState<StorageAclChange>();
  useReportStorageDialogGuard(onGuardChange, dirty, starter.pending);
  useEffect(() => { if (query.data) setRootAcl((current) => current ?? { acl: query.data!, key: Date.now() }); }, [query.data]);
  const root = hnsPathLabel(resource);
  const lockedReason = readOnly ? STORAGE_READ_ONLY_REASON : undefined;
  const onDirtyChange = useCallback((next: boolean) => setDirty(next), []);

  const close = () => { starter.cancel(); onClose(); };
  const reload = () => { setFailure(undefined); setRootAcl(undefined); setDirty(false); version.reload(); query.reload(); };
  const run = async (change: StorageAclChange) => {
    if (!version.etag || !rootAcl) return;
    setFailure(undefined);
    const flags = { ownerChanged: false, groupChanged: false, maskChanged: maskKey(rootAcl.acl.entries) !== maskKey(rootResultOf(rootAcl.acl.entries, change, hnsServiceFacts())) };
    const result = await starter.start(resource, change, version.etag);
    if (result.kind === 'stale') return;
    const source = aclEditSource(options.origin);
    if (result.kind === 'started') {
      trackAclEdit({ recursive: change.mode, outcome: 'started', ...flags, source });
      onClose();
      useTransfersPanelStore.getState().show('propagate');
      return;
    }
    trackAclEdit({ recursive: change.mode, outcome: outcomeOf(result.error), ...flags, source });
    setFailure(result.error);
  };
  const requestStart = (change: StorageAclChange) => {
    if (change.mode === 'modify') { void run(change); return; }
    setConfirming(change);
  };
  const confirmCopy = confirming
    ? propagateConfirmParts({ root, shortEtag: version.etag && shortEtag(version.etag), facts: hnsServiceFacts(), rootIdentity: aclRootIdentity(resource), change: confirming, displayNames: rootAcl?.acl.displayNames })
    : undefined;
  const blockedReason = failure?.code === 'TargetChanged' ? 'Reload before starting'
    : version.loading ? 'Reading the directory…' : version.error ? version.error.message : undefined;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hns-propagate-dialog">
        <DialogHeader>
          <DialogTitle>Propagate access</DialogTitle>
          <DialogDescription>Directory <span className="font-mono">{root}</span> and everything under it</DialogDescription>
        </DialogHeader>
        <QueuedDialogNotice />
        {readOnly && <p className="text-xs text-muted-foreground" data-testid="hns-propagate-read-only">{STORAGE_READ_ONLY_REASON}</p>}
        {!rootAcl && query.isLoading && <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="hns-propagate-loading"><RandomDotmSpinner size={14} /> Loading access control…</div>}
        {(query.error && !rootAcl) || version.error || failure ? (
          <div className="grid gap-2 text-xs text-destructive" role="alert" data-testid="hns-propagate-error">
            {failure?.fix?.kind === 'needs-role' && <span className="w-fit rounded bg-destructive/10 px-1.5 py-0.5 font-medium" data-testid="hns-propagate-needs-role">Needs {failure.fix.role}</span>}
            <span>{(failure ?? version.error ?? query.error)?.message}</span>
            <Button type="button" variant="outline" size="xs" className="w-fit" onClick={reload} data-testid="hns-propagate-reload">Reload</Button>
          </div>
        ) : null}
        {rootAcl && (
          <div hidden={!!confirming}>
            <PropagateAclForm key={rootAcl.key} rootAcl={rootAcl.acl} root={root} shortEtag={version.etag ? shortEtag(version.etag) : undefined} prefill={options.propagateChange}
              lockedReason={lockedReason} blockedReason={blockedReason} pending={starter.pending} onDirtyChange={onDirtyChange} onStart={requestStart} onCancel={close} />
          </div>
        )}
        {confirming && confirmCopy && (
          <TypedNameConfirmPanel
            title={confirming.mode === 'set' ? `Replace the access control of ${root} recursively?` : `Remove entries from ${root} recursively?`}
            lines={confirmCopy.lines} details={confirmCopy.details}
            name={propagateTypedName(resource)} confirmLabel="Start" pending={starter.pending}
            onConfirm={() => { const change = confirming; setConfirming(undefined); void run(change); }}
            onCancel={() => setConfirming(undefined)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
